/* global HTMLElement */
import React, { Fragment, useEffect, useRef, useState } from 'react'
import FocusTrap from 'focus-trap-react'
import PropTypes from 'prop-types'
import { classNames, Icon, iconProps, useTheme } from '@zambezi/sdk/common'
import { ariaHidden, attrModalHidden } from '../common/constants'

/**
 * Create a React Modal Wrapper component
 *
 * @param {import('./interfaces').ModalWrapperProps} props
 * @returns {JSX.Element}
 */
function ModalWrapper (props) {
  const containerRef = useRef(null)
  const {
    open,
    onOpen,
    title,
    children,
    confirm,
    cancel,
    isClosable,
    withBackdrop,
    withSectioning,
    withPadding,
    className,
    elementsId,
    elementRef,
    onCancel,
    onConfirm,
    iconProps,
    renderHeader = (title, titleId, closeButton) => (
      <Fragment>
        <h2 className='zb-modal-card-title' id={titleId}>
          {title}
        </h2>
        {isClosable && closeButton}
      </Fragment>
    ),
    renderFooter = confirm || cancel
      ? (handleConfirm, handleClose) => {
        return (
          <div className='zb-modal-card-foot-actions'>
            {!!cancel && (
              <button
                className='zb-button zb-button-secondary zb-modal-action'
                onClick={handleClose}
              >
                {cancel}
              </button>
            )}
            {!!confirm && (
              <button
                className='zb-button zb-button-primary zb-modal-action'
                onClick={handleConfirm}
              >
                {confirm}
              </button>
            )}
          </div>
        )
      }
      : null
  } = props
  const [element, setElement] = useState(elementRef.current)
  const theme = useTheme(element)

  useEffect(componentDidMount, [elementRef])
  useEffect(onOpenChanged, [open])

  const modalClass = classNames('zb-modal', className, {
    'zb-modal-is-active': open
  })

  const footerClass = classNames('zb-modal-card-foot', {
    'zb-modal-is-sectioned': withSectioning
  })

  const bodyClassName = classNames(
    'zb-modal-body',
    {
      'zb-modal-card-body': withPadding
    },
    'zb-has-scrollbar'
  )

  const closeButton = (
    <button className='zb-modal-close' onClick={onCancel} aria-label='Close dialog'>
      <Icon name='core-modal-close' aria-hidden='true' focusable='false' title='Close' iconProps={iconProps} />
    </button>
  )

  const modalHasHeader = renderHeader || title
  const titleId = `zb-modal-title-${elementsId}`
  const bodyId = `zb-modal-body-${elementsId}`

  return (
    <div
      className={`zb-modal-wrapper ${theme}`}
      aria-hidden={!open}
      ref={containerRef}
    >
      <FocusTrap active={withBackdrop && open}>
        <div className={modalClass} aria-hidden={!open}>
          {withBackdrop && (
            <div
              className='zb-modal-background'
              onClick={isClosable ? onCancel : null}
            />
          )}

          <div
            className='zb-modal-card'
            aria-modal='true'
            aria-labelledby={titleId}
            role='dialog'
          >
            {modalHasHeader ? (
              <div className='zb-modal-card-head'>
                {renderHeader && renderHeader(title, titleId, closeButton)}
              </div>
            ) : null}

            {!modalHasHeader && isClosable && closeButton}

            <section className={bodyClassName} id={bodyId} role='region'>
              {children}
            </section>

            {renderFooter && (
              <div className={footerClass}>
                {renderFooter(onConfirm, onCancel)}
              </div>
            )}
          </div>
        </div>
      </FocusTrap>
    </div>
  )

  function componentDidMount () {
    let container = document.getElementById(bodyId)
    setElement(elementRef.current)
    onOpen && onOpen()
    if (container.scrollHeight > container.clientHeight || container.scrollWidth >= container.clientWidth) {
      container.setAttribute('tabindex', '0')
    }
  }

  function onOpenChanged () {
    const { current: container } = containerRef
    const parentNode = container.parentNode

    updateAriaHidden()
    return updateAriaHidden

    function updateAriaHidden () {
      if (parentNode) {
        ;[...parentNode.childNodes].forEach(child => {
          if (
            child !== container &&
            child.tagName &&
            child.tagName !== 'SCRIPT'
          ) {
            let dataHidden = false
            if (!child.hasAttribute(attrModalHidden)) {
              child.setAttribute(
                attrModalHidden,
                child.hasAttribute(ariaHidden)
                  ? child.getAttribute(ariaHidden)
                  : 'null'
              )
            } else {
              dataHidden = child.getAttribute(attrModalHidden)
            }
            if (dataHidden === 'null') {
              child.removeAttribute(attrModalHidden)
              child.removeAttribute(ariaHidden)
            } else {
              child.setAttribute(ariaHidden, open ? true : dataHidden)
            }
          }
        })
      }
    }
  }
}

ModalWrapper.propTypes = {
  onOpen: PropTypes.func,
  onCancel: PropTypes.func,
  onConfirm: PropTypes.func,
  title: PropTypes.string,
  confirm: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool,
  cancel: PropTypes.string,
  withBackdrop: PropTypes.bool,
  withSectioning: PropTypes.bool,
  withPadding: PropTypes.bool,
  isClosable: PropTypes.bool,
  className: PropTypes.string,
  renderHeader: PropTypes.func,
  renderFooter: PropTypes.func,
  elementRef: PropTypes.shape({ current: PropTypes.instanceOf(HTMLElement) }),
  elementsId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  iconProps
}

export default ModalWrapper
